import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../components/common/Input";
import TextArea from "../../../components/common/TextArea";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import CategoryHierarchyDropdown from "./CategoryHierarchyDropdown";
import { getCategoriesForDropdownThunk } from "../../../store/thunks/categoryThunks";
import {
  PhotographIcon,
  XIcon,
  UploadIcon,
  TrashIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";

const CategoryForm = ({
  category,
  onSubmit,
  onCancel,
  serverError,
  defaultParentId = null, // Pre-select parent when adding subcategory
}) => {
  const dispatch = useDispatch();
  const { dropdownCategories, dropdownLoading } = useSelector(
    (state) => state.category
  );

  const [formData, setFormData] = useState({
    // Basic Information
    name: "",
    slug: "",
    description: "",
    tags: "",
    parentId: null, // Store as string ID or null (CategoryHierarchyDropdown uses IDs)
    order: 0,

    // Classification
    productTypes: [],
    productSubTypes: [],
    targetGender: "ALL",
    occasions: [],
    collections: [],

    // Display Configuration
    isActive: true,
    showInMenu: true,
    showInMobile: true,
    showInHomepage: false,
    isFeatured: false,
    displayMode: "GRID",

    // Availability
    allBranches: true,
    specificBranches: [],
    specificStores: [],

    // Pricing Configuration
    allowProductOverride: true,
    inheritFromParent: true,
    pricingPriority: 0,

    // SEO
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
  });

  // Image state
  const [imageFiles, setImageFiles] = useState({
    icon: null,
    banner: null,
    thumbnail: null,
    mobile: null,
    gallery: [],
  });

  const [imagePreviews, setImagePreviews] = useState({
    icon: null,
    banner: null,
    thumbnail: null,
    mobile: null,
    gallery: [],
  });

  const [imagesToDelete, setImagesToDelete] = useState([]);

  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("basic");

  // Check if this is a new subcategory creation (has _isNewSubcategory flag)
  const isNewSubcategory = category?._isNewSubcategory === true;

  // Fetch dropdown categories on mount
  // Exclude current category when editing, exclude nothing when creating new
  useEffect(() => {
    const excludeId = isNewSubcategory ? null : category?._id;
    dispatch(getCategoriesForDropdownThunk(excludeId));
  }, [dispatch, category?._id, isNewSubcategory]);

  // Set default parent when we have defaultParentId (for new subcategory)
  useEffect(() => {
    if (isNewSubcategory && defaultParentId) {
      setFormData((prev) => ({
        ...prev,
        parentId: defaultParentId,
      }));
    }
  }, [isNewSubcategory, defaultParentId]);

  // Initialize form with category data when editing
  useEffect(() => {
    if (category && !isNewSubcategory) {
      // Extract parentId as string
      const parentIdValue = category.parentId?._id || category.parentId || null;

      setFormData({
        name: category.name || "",
        slug: category.slug || "",
        description: category.description || "",
        tags: category.tags?.join(", ") || "",
        parentId: parentIdValue,
        order: category.order || 0,

        productTypes: category.classification?.productTypes || [],
        productSubTypes: category.classification?.productSubTypes || [],
        targetGender: category.classification?.targetGender || "ALL",
        occasions: category.classification?.occasions || [],
        collections: category.classification?.collections || [],

        isActive: category.display?.isActive !== false,
        showInMenu: category.display?.showInMenu !== false,
        showInMobile: category.display?.showInMobile !== false,
        showInHomepage: category.display?.showInHomepage || false,
        isFeatured: category.display?.isFeatured || false,
        displayMode: category.display?.displayMode || "GRID",

        allBranches: category.availability?.allBranches !== false,
        specificBranches: category.availability?.specificBranches || [],
        specificStores: category.availability?.specificStores || [],

        allowProductOverride:
          category.pricingConfig?.allowProductOverride !== false,
        inheritFromParent: category.pricingConfig?.inheritFromParent !== false,
        pricingPriority: category.pricingConfig?.priority || 0,

        metaTitle: category.seo?.metaTitle || "",
        metaDescription: category.seo?.metaDescription || "",
        metaKeywords: category.seo?.metaKeywords?.join(", ") || "",
      });

      // Set existing image previews
      setImagePreviews({
        icon: category.images?.icon?.url || null,
        banner: category.images?.banner?.url || null,
        thumbnail: category.images?.thumbnail?.url || null,
        mobile: category.images?.mobile?.url || null,
        gallery: category.images?.gallery?.map((img) => img.url) || [],
      });
    }
  }, [category, isNewSubcategory]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  }, []);

  // Handle parent category dropdown change (CategoryHierarchyDropdown returns ID)
  const handleParentChange = useCallback((selectedId) => {
    setFormData((prev) => ({
      ...prev,
      parentId: selectedId || null,
    }));
  }, []);

  // Handle single image upload
  const handleImageChange = (field, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        [field]: "Please select an image file",
      }));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        [field]: "Image size should be less than 5MB",
      }));
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreviews((prev) => ({
        ...prev,
        [field]: reader.result,
      }));
    };
    reader.readAsDataURL(file);

    setImageFiles((prev) => ({
      ...prev,
      [field]: file,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  // Handle gallery images
  const handleGalleryChange = (files) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter((file) => {
      if (!file.type.startsWith("image/")) {
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        return false;
      }
      return true;
    });

    if (validFiles.length !== files.length) {
      setErrors((prev) => ({
        ...prev,
        gallery: "Some files were skipped (invalid type or size > 5MB)",
      }));
    }

    // Create previews
    const newPreviews = [];
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        if (newPreviews.length === validFiles.length) {
          setImagePreviews((prev) => ({
            ...prev,
            gallery: [...prev.gallery, ...newPreviews],
          }));
        }
      };
      reader.readAsDataURL(file);
    });

    setImageFiles((prev) => ({
      ...prev,
      gallery: [...prev.gallery, ...validFiles],
    }));
  };

  // Remove image
  const handleRemoveImage = (field, index = null) => {
    if (field === "gallery" && index !== null) {
      // Remove from gallery
      setImageFiles((prev) => ({
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== index),
      }));
      setImagePreviews((prev) => ({
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== index),
      }));

      // If editing and removing existing image, mark for deletion
      if (
        category?.images?.gallery?.[index] &&
        imagePreviews.gallery[index] === category.images.gallery[index].url
      ) {
        setImagesToDelete((prev) => [
          ...prev,
          { type: "gallery", index, url: category.images.gallery[index].url },
        ]);
      }
    } else {
      // Remove single image
      setImageFiles((prev) => ({
        ...prev,
        [field]: null,
      }));
      setImagePreviews((prev) => ({
        ...prev,
        [field]: null,
      }));

      // If editing and removing existing image, mark for deletion
      if (
        category?.images?.[field]?.url &&
        imagePreviews[field] === category.images[field].url
      ) {
        setImagesToDelete((prev) => [
          ...prev,
          { type: field, url: category.images[field].url },
        ]);
      }
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    }
    if (formData.order < 0) {
      newErrors.order = "Order must be a positive number.";
    }
    if (formData.pricingPriority < 0) {
      newErrors.pricingPriority = "Priority must be a positive number.";
    }
    return newErrors;
  }, [formData]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const formErrors = validateForm();
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);
        setActiveTab("basic");
      } else {
        // Prepare form data for submission
        const submitData = new FormData();

        // Basic data
        submitData.append("name", formData.name);
        if (formData.slug) submitData.append("slug", formData.slug);
        if (formData.description)
          submitData.append("description", formData.description);

        // Handle parentId
        if (formData.parentId && formData.parentId.trim() !== "") {
          submitData.append("parentId", formData.parentId);
        } else {
          submitData.append("parentId", "null"); // Send string "null" which backend will convert
        }
        submitData.append("order", formData.order);
        if (formData.tags) {
          submitData.append(
            "tags",
            JSON.stringify(formData.tags.split(",").map((tag) => tag.trim()))
          );
        }

        // Classification
        submitData.append(
          "classification",
          JSON.stringify({
            productTypes: formData.productTypes,
            productSubTypes: formData.productSubTypes,
            targetGender: formData.targetGender,
            occasions: formData.occasions,
            collections: formData.collections,
          })
        );

        // Display
        submitData.append(
          "display",
          JSON.stringify({
            isActive: formData.isActive,
            showInMenu: formData.showInMenu,
            showInMobile: formData.showInMobile,
            showInHomepage: formData.showInHomepage,
            isFeatured: formData.isFeatured,
            displayMode: formData.displayMode,
          })
        );

        // Availability
        submitData.append(
          "availability",
          JSON.stringify({
            allBranches: formData.allBranches,
            specificBranches: formData.specificBranches,
            specificStores: formData.specificStores,
          })
        );

        // Pricing Configuration
        submitData.append(
          "pricingConfig",
          JSON.stringify({
            allowProductOverride: formData.allowProductOverride,
            inheritFromParent: formData.inheritFromParent,
            priority: formData.pricingPriority,
            branchSpecificRules: [],
          })
        );

        // SEO
        submitData.append(
          "seo",
          JSON.stringify({
            metaTitle: formData.metaTitle || undefined,
            metaDescription: formData.metaDescription || undefined,
            metaKeywords: formData.metaKeywords
              ? formData.metaKeywords.split(",").map((k) => k.trim())
              : undefined,
          })
        );

        // Append image files
        if (imageFiles.icon) {
          submitData.append("icon", imageFiles.icon);
        }
        if (imageFiles.banner) {
          submitData.append("banner", imageFiles.banner);
        }
        if (imageFiles.thumbnail) {
          submitData.append("thumbnail", imageFiles.thumbnail);
        }
        if (imageFiles.mobile) {
          submitData.append("mobile", imageFiles.mobile);
        }
        imageFiles.gallery.forEach((file) => {
          submitData.append("gallery", file);
        });

        // Images to delete
        if (imagesToDelete.length > 0) {
          submitData.append("imagesToDelete", JSON.stringify(imagesToDelete));
        }

        onSubmit(submitData);
      }
    },
    [formData, imageFiles, imagesToDelete, onSubmit, validateForm]
  );

  const tabs = [
    { id: "basic", label: "Basic Info" },
    { id: "images", label: "Images" },
    { id: "classification", label: "Classification" },
    { id: "display", label: "Display" },
    { id: "availability", label: "Availability" },
    { id: "pricing", label: "Pricing" },
    { id: "seo", label: "SEO" },
  ];

  // Get selected parent name for info banner
  const selectedParentName = useMemo(() => {
    const parentId = formData.parentId || defaultParentId;
    if (parentId && dropdownCategories.length > 0) {
      const found = dropdownCategories.find((cat) => cat._id === parentId);
      return found?.name;
    }
    return null;
  }, [formData.parentId, defaultParentId, dropdownCategories]);

  // Image upload component
  const ImageUploadField = ({ label, field, helperText, preview }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {helperText && <p className="text-xs text-gray-500">{helperText}</p>}

      {preview ? (
        <div className="relative inline-block">
          <img
            src={preview}
            alt={label}
            className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
          />
          <button
            type="button"
            onClick={() => handleRemoveImage(field)}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors">
            <UploadIcon className="w-4 h-4" />
            <span>Choose File</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(field, e.target.files[0])}
              className="hidden"
            />
          </label>
        </div>
      )}

      {errors[field] && <p className="text-sm text-red-600">{errors[field]}</p>}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Show parent info banner when adding subcategory */}
      {isNewSubcategory && selectedParentName && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
          <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            Creating subcategory under:{" "}
            <span className="font-semibold">{selectedParentName}</span>
          </p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-2 border-b overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? "text-primary border-b-2 border-primary"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Basic Info Tab */}
      {activeTab === "basic" && (
        <div className="space-y-4">
          {/* Category Hierarchy Dropdown for Parent Selection */}
          <CategoryHierarchyDropdown
            label="Parent Category"
            categories={dropdownCategories}
            value={formData.parentId}
            onChange={handleParentChange}
            placeholder="Select parent category (or leave empty for root)"
            maxPathLength={50}
            allowClear={true}
            disabled={dropdownLoading}
          />

          {dropdownLoading && (
            <p className="text-xs text-gray-500 -mt-2">Loading categories...</p>
          )}

          <Input
            label="Name *"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter category name"
            error={errors.name}
            autoFocus={isNewSubcategory}
          />

          <Input
            label="Slug (URL-friendly name)"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            placeholder="auto-generated-from-name"
            helperText="Leave empty to auto-generate"
          />

          <TextArea
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter category description"
            rows={3}
          />

          <Input
            label="Tags (comma separated)"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            placeholder="gold, wedding, traditional"
          />

          <Input
            label="Display Order"
            name="order"
            type="number"
            value={formData.order}
            onChange={handleChange}
            error={errors.order}
            helperText="Lower numbers appear first"
          />
        </div>
      )}

      {/* Images Tab */}
      {activeTab === "images" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ImageUploadField
              label="Icon"
              field="icon"
              helperText="Small icon for menus (recommended: 64x64px)"
              preview={imagePreviews.icon}
            />

            <ImageUploadField
              label="Thumbnail"
              field="thumbnail"
              helperText="Category card thumbnail (recommended: 400x400px)"
              preview={imagePreviews.thumbnail}
            />

            <ImageUploadField
              label="Banner"
              field="banner"
              helperText="Large banner image (recommended: 1920x600px)"
              preview={imagePreviews.banner}
            />

            <ImageUploadField
              label="Mobile Image"
              field="mobile"
              helperText="Mobile-optimized image (recommended: 800x800px)"
              preview={imagePreviews.mobile}
            />
          </div>

          {/* Gallery Images */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Gallery Images
            </label>
            <p className="text-xs text-gray-500">
              Multiple images for category showcase (max 10 images)
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {imagePreviews.gallery.map((preview, index) => (
                <div key={index} className="relative">
                  <img
                    src={preview}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage("gallery", index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    #{index + 1}
                  </div>
                </div>
              ))}

              {imagePreviews.gallery.length < 10 && (
                <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-gray-50 transition-colors">
                  <PhotographIcon className="w-8 h-8 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-2">Add Images</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleGalleryChange(e.target.files)}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {errors.gallery && (
              <p className="text-sm text-red-600">{errors.gallery}</p>
            )}
          </div>
        </div>
      )}

      {/* Classification Tab */}
      {activeTab === "classification" && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Gender
            </label>
            <select
              name="targetGender"
              value={formData.targetGender}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="ALL">All</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="KIDS">Kids</option>
            </select>
          </div>

          <div className="text-sm text-gray-500">
            Note: Product types, occasions, and collections can be configured
            after creation.
          </div>
        </div>
      )}

      {/* Display Tab */}
      {activeTab === "display" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="mr-2"
              />
              <span>Active (Visible to users)</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="showInMenu"
                checked={formData.showInMenu}
                onChange={handleChange}
                className="mr-2"
              />
              <span>Show in Navigation Menu</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="showInMobile"
                checked={formData.showInMobile}
                onChange={handleChange}
                className="mr-2"
              />
              <span>Show in Mobile App</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="showInHomepage"
                checked={formData.showInHomepage}
                onChange={handleChange}
                className="mr-2"
              />
              <span>Show on Homepage</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleChange}
                className="mr-2"
              />
              <span>Featured Category</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Mode
            </label>
            <select
              name="displayMode"
              value={formData.displayMode}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="GRID">Grid</option>
              <option value="LIST">List</option>
              <option value="CAROUSEL">Carousel</option>
              <option value="BANNER">Banner</option>
            </select>
          </div>
        </div>
      )}

      {/* Availability Tab */}
      {activeTab === "availability" && (
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="allBranches"
              checked={formData.allBranches}
              onChange={handleChange}
              className="mr-2"
            />
            <span>Available at All Branches</span>
          </label>

          {!formData.allBranches && (
            <div className="text-sm text-gray-500">
              Note: Specific branch/store selection will be available after
              creation.
            </div>
          )}
        </div>
      )}

      {/* Pricing Tab */}
      {activeTab === "pricing" && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Pricing Configuration</p>
                <p>
                  Set how pricing rules work for this category. Branch-specific
                  pricing rules can be assigned after creating the category.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="allowProductOverride"
                checked={formData.allowProductOverride}
                onChange={handleChange}
                className="mr-2"
              />
              <div>
                <span className="font-medium">Allow Product Override</span>
                <p className="text-sm text-gray-500">
                  Products in this category can have their own pricing rules
                  that override category rules
                </p>
              </div>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="inheritFromParent"
                checked={formData.inheritFromParent}
                onChange={handleChange}
                className="mr-2"
              />
              <div>
                <span className="font-medium">Inherit from Parent</span>
                <p className="text-sm text-gray-500">
                  If no pricing rule is set, inherit from parent category
                </p>
              </div>
            </label>

            <Input
              label="Pricing Priority"
              name="pricingPriority"
              type="number"
              value={formData.pricingPriority}
              onChange={handleChange}
              error={errors.pricingPriority}
              helperText="Higher priority rules are applied first when multiple categories match"
            />
          </div>

          {category?._id && !isNewSubcategory && (
            <div className="border-t pt-4">
              <p className="text-sm text-gray-600 mb-2">
                Branch-specific pricing rules:{" "}
                {category.pricingConfig?.branchSpecificRules?.length || 0}{" "}
                configured
              </p>
              <p className="text-xs text-gray-500">
                Manage branch-specific pricing rules from the category details
                page after saving.
              </p>
            </div>
          )}
        </div>
      )}

      {/* SEO Tab */}
      {activeTab === "seo" && (
        <div className="space-y-4">
          <Input
            label="Meta Title"
            name="metaTitle"
            value={formData.metaTitle}
            onChange={handleChange}
            placeholder="SEO title for search engines"
            maxLength={60}
          />

          <TextArea
            label="Meta Description"
            name="metaDescription"
            value={formData.metaDescription}
            onChange={handleChange}
            placeholder="SEO description for search engines"
            rows={2}
            maxLength={160}
          />

          <Input
            label="Meta Keywords (comma separated)"
            name="metaKeywords"
            value={formData.metaKeywords}
            onChange={handleChange}
            placeholder="keyword1, keyword2, keyword3"
          />
        </div>
      )}

      {/* Error Display */}
      {serverError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {serverError}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          {category && !isNewSubcategory
            ? "Update Category"
            : "Create Category"}
        </Button>
      </div>
    </form>
  );
};

export default React.memo(CategoryForm);
